import React, { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { submitQuote } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Devis() {
  const { t } = useApp();
  const [form, setForm] = useState({ nom: "", email: "", societe: "", telephone: "", besoin: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { nom: form.nom, email: form.email, besoin: form.besoin };
      if (form.societe) payload.societe = form.societe;
      if (form.telephone) payload.telephone = form.telephone;
      const res = await submitQuote(payload);
      setDone(true);
      toast.success(res.message || t.devis.success);
    } catch {
      toast.error(t.devis.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-16 sm:px-8" data-testid="devis-page">
      <header className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cea-accent">{t.nav.devis}</p>
        <h1 className="mt-3 text-4xl font-extrabold tracking-tighter text-cea-ink sm:text-5xl">{t.devis.title}</h1>
        <p className="mt-3 text-slate-500">{t.devis.sub}</p>
      </header>

      {done ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center gap-4 rounded-3xl border border-slate-200 bg-cea-surface p-12 text-center"
          data-testid="devis-success"
        >
          <CheckCircle2 className="h-14 w-14 text-cea-accent" />
          <p className="text-lg font-semibold text-cea-ink">{t.devis.success}</p>
        </motion.div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="devis-form">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="nom">{t.devis.nom} *</Label>
              <Input id="nom" required value={form.nom} onChange={set("nom")} data-testid="devis-nom" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">{t.devis.email} *</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="devis-email" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="societe">{t.devis.societe}</Label>
              <Input id="societe" value={form.societe} onChange={set("societe")} data-testid="devis-societe" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="telephone">{t.devis.telephone}</Label>
              <Input id="telephone" value={form.telephone} onChange={set("telephone")} data-testid="devis-telephone" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label htmlFor="besoin">{t.devis.besoin} *</Label>
            <Textarea id="besoin" required rows={5} value={form.besoin} onChange={set("besoin")} data-testid="devis-besoin" className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading} data-testid="devis-submit" className="w-full rounded-full bg-cea-accent py-6 text-base font-semibold text-white hover:bg-cea-navy sm:w-auto sm:px-10">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t.devis.submit}
          </Button>
        </form>
      )}
    </div>
  );
}
